/**
 * reco-engine.js
 *
 * Recommendation engine for the cart recommendation feature ("Preporuceno za tebe").
 * Pure logic + data fetching only - no DOM rendering. Loaded only on pages/blocks that
 * render recommendations (wired up in later tasks). Reads activity data written by
 * reco-tracker.js (window.MtgReco.store) and blends it with Shopify's own storefront
 * endpoints to produce a ranked, deduped, exclusion-filtered list of products.
 *
 * Public API added to window.MtgReco (does not touch window.MtgReco.store, which
 * reco-tracker.js owns):
 *   window.MtgReco.getRecommendations({ limit, fallbackCollection }) -> Promise<Array>
 *   window.MtgReco.invalidateRecommendations()  - drops the short-lived memo below
 *
 * Each resolved item has the shape:
 *   { handle, title, url, image, price, currency, variantId, variantsCount, available, source }
 * where `source` is one of "removed" | "viewed" | "shopify" | "collection" (kept for
 * debugging/QA - not meant to be shown to shoppers).
 *
 * Cascade (fills up to `limit`, deduping by handle and excluding anything already in
 * the cart, stopping as soon as `limit` items are confirmed):
 *   1. store.getRemoved()  - newest-first, hydrated via /products/<handle>.js
 *   2. store.getViewed()   - sorted by interest (store.interest, glances dropped), hydrated
 *                            the same way; older trackers: view count desc, then last-viewed desc.
 *                            With a non-empty cart: at most 1 slot, interest >= 0.3 only
 *                            (no synergy check here, so the cart-based level 3 gets the room)
 *   3. Shopify's own recommendations for the most expensive line item in the cart:
 *      /recommendations/products.json?product_id=<id>&limit=<batchLimit>
 *   4. First available products from the fallback collection:
 *      /collections/<fallbackCollection>/products.json?limit=<batchLimit>
 *      (batchLimit = max(8, limit * 2), so a bigger `limit` still has enough
 *      headroom left after in-cart/sold-out exclusions from that one batch)
 *
 * Notes on trust boundaries:
 *   - reco-tracker.js prunes on WRITE, not on read - getRemoved() can still hand back
 *     entries older than 7 days on a page where no write happened since. This engine
 *     independently filters those out; viewed entries are filtered by current interest.
 *   - Prices/availability are never trusted from localStorage; every candidate handle is
 *     re-fetched fresh from the storefront (/products/<handle>.js) before being used, and
 *     levels 3/4 use the live product payloads Shopify returns directly.
 *   - /products/<handle>.js calls (levels 1-2 only) are capped at 8 total per
 *     getRecommendations() call, regardless of how many candidate handles exist.
 */
(function () {
  'use strict';

  var DEFAULT_LIMIT = 3;
  var DEFAULT_COLLECTION = 'commander-staples'; // verified via Shopify admin: title "Commander Staples"
  var MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days - mirrors reco-tracker.js's own cutoff
  var MAX_HYDRATIONS = 8;
  // With something in the cart the drawer is about the cart: level 2 (viewed cards, which this
  // engine cannot check for synergy with the cart) gets at most this many slots, and only for
  // cards the visitor was really into; Shopify's cart-based recommendations fill the rest.
  var MAX_VIEWED_WITH_CART = 1;
  var MIN_INTEREST_WITH_CART = 0.3; // cap on /products/<handle>.js calls per getRecommendations() call
  var IMAGE_WIDTH = 300; // ~300px featured image, via Shopify CDN's `width` query param

  // ---------------------------------------------------------------------
  // Small utilities
  // ---------------------------------------------------------------------

  function debugLog() {
    try {
      if (window.console && typeof window.console.debug === 'function') {
        var args = ['[reco-engine]'].concat(Array.prototype.slice.call(arguments));
        window.console.debug.apply(window.console, args);
      }
    } catch (e) {
      // no-op: logging must never throw.
    }
  }

  /** "Already viewed" line for a level 1/2 card (reco-tracker.js seenLabel), '' when unknown. */
  function seenReason(kind, entry) {
    try {
      var store = window.MtgReco && window.MtgReco.store;
      if (!store || typeof store.seenLabel !== 'function') return '';
      var lang = (document.documentElement && document.documentElement.lang) || 'sr';
      return store.seenLabel(kind, entry, lang) || '';
    } catch (e) {
      return '';
    }
  }

  function getCurrency() {
    try {
      if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
        return window.Shopify.currency.active;
      }
    } catch (e) {
      // fall through to default
    }
    return 'RSD';
  }

  function resizeImageUrl(url, width) {
    if (!url || typeof url !== 'string') return null;
    try {
      var stripped = url.replace(/([?&])width=[^&]*&?/g, '$1').replace(/[?&]$/, '');
      var separator = stripped.indexOf('?') === -1 ? '?' : '&';
      return stripped + separator + 'width=' + width;
    } catch (e) {
      return url;
    }
  }

  function getFeaturedImageUrl(product) {
    var raw = null;
    if (product && typeof product.featured_image === 'string' && product.featured_image) {
      raw = product.featured_image;
    } else if (product && Array.isArray(product.images) && product.images.length) {
      var first = product.images[0];
      raw = typeof first === 'string' ? first : (first && first.src) || null;
    }
    return resizeImageUrl(raw, IMAGE_WIDTH);
  }

  /**
   * Fetches a URL and resolves to `{ data, status }`, where `data` is the parsed
   * JSON body or `null` on any failure (network error, non-2xx response, non-JSON
   * body), and `status` is the response's HTTP status (0 on network error, i.e. no
   * response at all). Never rejects. Callers that need to react to the status of
   * their own request (e.g. a 429 retry) should use this instead of fetchJSON, since
   * a single shared "last status" would race across concurrent in-flight requests.
   */
  function fetchJSONWithStatus(url) {
    return window
      .fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res || !res.ok) {
          var status = res ? res.status : 0;
          debugLog('non-OK response, skipping', url, status);
          return { data: null, status: status };
        }
        return res
          .json()
          .then(function (json) {
            return { data: json, status: res.status };
          })
          .catch(function (err) {
            debugLog('failed to parse JSON', url, err);
            return { data: null, status: res.status };
          });
      })
      .catch(function (err) {
        debugLog('fetch failed', url, err);
        return { data: null, status: 0 };
      });
  }

  /**
   * Fetches a URL and resolves to its parsed JSON body, or `null` on any failure
   * (network error, non-2xx response, non-JSON body). Never rejects - every level
   * of the cascade treats a `null` result as "this call failed, move on".
   */
  function fetchJSON(url) {
    return fetchJSONWithStatus(url).then(function (r) { return r.data; });
  }

  // ---------------------------------------------------------------------
  // Product normalization (shared by all four cascade levels)
  // ---------------------------------------------------------------------

  /**
   * The storefront JSON endpoints DISAGREE on price units, which is easy to miss
   * because the product object shape is otherwise identical:
   *   - /products/<handle>.js          -> integer minor units   (6000 == 60,00 RSD)
   *   - /recommendations/products.json -> integer minor units   (6000)
   *   - /collections/<h>/products.json -> decimal STRING        ("60.00")
   * Cascade level 4 (the fallback collection) is the only one hitting the third
   * endpoint, so before this normalization a level-4 card rendered "60.00" as 60
   * minor units and displayed "1 RSD" instead of "60 RSD". Everything the engine
   * emits is minor units; the UI divides by 100 exactly once.
   */
  function toMinorUnits(price) {
    if (typeof price === 'string') {
      var parsed = parseFloat(price);
      return isFinite(parsed) ? Math.round(parsed * 100) : NaN;
    }
    return Number(price);
  }

  /**
   * Normalizes a raw Shopify product JSON payload (from /products/<handle>.js,
   * /recommendations/products.json, or /collections/<handle>/products.json - all
   * three use the same product object shape, but not the same price units, see
   * toMinorUnits above) into the engine's output shape.
   * Returns null if the product has no available variant (sold out / deleted),
   * so callers can just skip a null result.
   *
   * Variant selection (variantId/price), by priority among AVAILABLE variants:
   *   1. title trimmed equals "Near Mint" (case-insensitive)
   *   2. title starts with "Near Mint" (i.e. the foil printing, "Near Mint - Foil")
   *   3. otherwise the cheapest available variant (NaN-safe comparison below)
   * This card is always a direct add-to-cart, so exactly one variant is chosen
   * here - variantsCount is kept only for compatibility, it no longer drives the UI.
   */
  function normalizeProduct(product, source) {
    if (!product || typeof product !== 'object' || !product.handle) return null;

    var variants = Array.isArray(product.variants) ? product.variants : [];
    var nmExact = null;
    var nmFoil = null;
    var cheapest = null;
    var cheapestPrice = NaN;
    var availableCount = 0;
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      if (!v || !v.available) continue;
      availableCount++;

      var title = typeof v.title === 'string' ? v.title.trim() : '';
      if (!nmExact && /^near mint$/i.test(title)) {
        nmExact = v;
      } else if (!nmFoil && /^near mint/i.test(title)) {
        nmFoil = v;
      }

      var variantPrice = toMinorUnits(v.price);
      // NaN comparisons are always false in both directions, so a naive
      // `variantPrice < cheapestPrice` would let a bad price get "stuck": once
      // cheapestPrice is NaN, no later price - even a perfectly valid one -
      // could ever compare less than it and take over. A variant is only
      // allowed to win when its own price parsed cleanly (isFinite), and it is
      // only compared against cheapestPrice when that also parsed cleanly -
      // otherwise (cheapestPrice still NaN) any finite price replaces it.
      if (cheapest === null || (isFinite(variantPrice) && (!isFinite(cheapestPrice) || variantPrice < cheapestPrice))) {
        cheapest = v;
        cheapestPrice = variantPrice;
      }
    }
    if (!cheapest) return null; // sold out - excluded per Global Constraints

    var chosen = nmExact || nmFoil || cheapest;
    var chosenPrice = chosen === cheapest ? cheapestPrice : toMinorUnits(chosen.price);

    return {
      handle: product.handle,
      title: product.title || '',
      url: typeof product.url === 'string' && product.url ? product.url : '/products/' + product.handle,
      image: getFeaturedImageUrl(product),
      price: chosenPrice,
      currency: getCurrency(),
      variantId: chosen.id,
      variantsCount: availableCount, // count of AVAILABLE variants only, kept for compatibility only
      available: true,
      source: source
    };
  }

  function hydrateHandle(handle, source) {
    return fetchJSON('/products/' + encodeURIComponent(handle) + '.js').then(function (product) {
      return normalizeProduct(product, source);
    });
  }

  // ---------------------------------------------------------------------
  // Activity data (levels 1-2 candidate lists)
  // ---------------------------------------------------------------------

  /** store.getRemoved() does not prune on read - filter stale entries here too. */
  function getFreshRemovedNewestFirst() {
    var cutoff = Date.now() - MAX_AGE_MS;
    var entries = [];
    try {
      var raw = window.MtgReco && window.MtgReco.store && window.MtgReco.store.getRemoved();
      if (Array.isArray(raw)) {
        raw.forEach(function (entry) {
          if (entry && typeof entry.h === 'string' && typeof entry.t === 'number' && entry.t >= cutoff) {
            entries.push(entry);
          }
        });
      }
    } catch (e) {
      debugLog('getRemoved() failed', e);
    }
    entries.sort(function (a, b) {
      return b.t - a.t; // newest first
    });
    return entries;
  }

  /**
   * Viewed products worth suggesting, most interesting first. With the tracker's interest
   * score (reco-tracker.js: time on the page, scroll, gallery/variant clicks, return visits,
   * add to cart, faded over time) glances are dropped and the server profile of a logged-in
   * customer counts too; without it, the old rule: last 7 days, view count then recency.
   */
  function getFreshViewedByCountThenRecency() {
    var nowMs = Date.now();
    var cutoff = nowMs - MAX_AGE_MS;
    var entries = [];
    var store = window.MtgReco && window.MtgReco.store;
    var scored = !!(store && typeof store.interest === 'function');
    var minInterest = scored && typeof store.INTEREST_MIN === 'number' ? store.INTEREST_MIN : 0;
    try {
      var raw = store && store.getViewed();
      if (raw && typeof raw === 'object') {
        Object.keys(raw).forEach(function (handle) {
          var entry = raw[handle];
          if (!entry || typeof entry.c !== 'number' || typeof entry.t !== 'number') return;
          if (scored) {
            var i = store.interest(entry, nowMs);
            if (i >= minInterest) entries.push({ handle: handle, c: entry.c, t: entry.t, i: i, raw: entry });
          } else if (entry.t >= cutoff) {
            entries.push({ handle: handle, c: entry.c, t: entry.t, raw: entry });
          }
        });
      }
    } catch (e) {
      debugLog('getViewed() failed', e);
    }
    entries.sort(function (a, b) {
      if (scored && b.i !== a.i) return b.i - a.i; // interest desc
      if (b.c !== a.c) return b.c - a.c; // count desc
      return b.t - a.t; // then last-touched desc
    });
    return entries;
  }

  // ---------------------------------------------------------------------
  // Cart state (fetched once per getRecommendations() call)
  // ---------------------------------------------------------------------

  function fetchCartOnce() {
    return fetchJSON('/cart.js').then(function (cart) {
      return cart && Array.isArray(cart.items) ? cart : { items: [] };
    });
  }

  function getCartHandleSet(cart) {
    var set = {};
    if (cart && Array.isArray(cart.items)) {
      cart.items.forEach(function (item) {
        if (item && item.handle) set[item.handle] = true;
      });
    }
    return set;
  }

  /** Seed for level 3: the most expensive line item in the cart (by line total). */
  function getSeedProductId(cart) {
    if (!cart || !Array.isArray(cart.items) || !cart.items.length) return null;
    var bestValue = -1;
    var bestId = null;
    cart.items.forEach(function (item) {
      if (!item || !item.product_id) return;
      var value = typeof item.line_price === 'number' ? item.line_price : Number(item.price) || 0;
      if (value > bestValue) {
        bestValue = value;
        bestId = item.product_id;
      }
    });
    return bestId;
  }

  // ---------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------

  /**
   * Runs the full four-level cascade. Always hits the network; callers go
   * through getRecommendations(), which memoizes this.
   */
  function runCascade(limit, fallbackCollection) {
    var results = [];
    var seenHandles = {};
    var hydrationCount = 0;
    // Levels 3/4 fetch one batch each (no per-item hydration call, unlike
    // levels 1-2) and dedupe/exclude in-cart items out of that same batch, so
    // a flat limit=8 could leave fewer than `limit` results once a bigger
    // `limit` (e.g. 6) collides with in-cart/sold-out exclusions. Always ask
    // for at least 8, or 2x the requested limit if that is bigger.
    var batchLimit = Math.max(8, limit * 2);

    function isFull() {
      return results.length >= limit;
    }

    function addIfEligible(item, cartHandleSet) {
      if (!item) return;
      if (isFull()) return;
      if (seenHandles[item.handle]) return; // dedupe across levels
      if (cartHandleSet[item.handle]) return; // exclude items already in cart
      seenHandles[item.handle] = true;
      results.push(item);
    }

    // store.ready(): a logged-in customer's server profile is in (max ~1.5 s, never rejects)
    var store = window.MtgReco && window.MtgReco.store;
    var profileReady = store && typeof store.ready === 'function' ? store.ready() : null;
    return Promise.all([fetchCartOnce(), profileReady])
      .then(function (r) {
        var cart = r[0];
        var cartHandleSet = getCartHandleSet(cart);

        // --- Level 1: removed items, newest-first ---
        var removedEntries = getFreshRemovedNewestFirst();

        function runLevel1(index) {
          if (isFull() || hydrationCount >= MAX_HYDRATIONS || index >= removedEntries.length) {
            return Promise.resolve();
          }
          var handle = removedEntries[index].h;
          if (seenHandles[handle] || cartHandleSet[handle]) {
            return runLevel1(index + 1);
          }
          hydrationCount++;
          return hydrateHandle(handle, 'removed').then(function (item) {
            if (item) item.reason = seenReason('removed', null);
            addIfEligible(item, cartHandleSet);
            return runLevel1(index + 1);
          });
        }

        // --- Level 2: viewed items, most interesting first (glances dropped) ---
        var cartHasItems = Object.keys(cartHandleSet).length > 0;
        var viewedAdded = 0;
        function runLevel2(index, viewedEntries) {
          if (isFull() || hydrationCount >= MAX_HYDRATIONS || index >= viewedEntries.length) {
            return Promise.resolve();
          }
          if (cartHasItems && viewedAdded >= MAX_VIEWED_WITH_CART) return Promise.resolve();
          var entry = viewedEntries[index];
          var handle = entry.handle;
          var tooWeak = cartHasItems && typeof entry.i === 'number' && entry.i < MIN_INTEREST_WITH_CART;
          if (seenHandles[handle] || cartHandleSet[handle] || tooWeak) {
            return runLevel2(index + 1, viewedEntries);
          }
          hydrationCount++;
          return hydrateHandle(handle, 'viewed').then(function (item) {
            if (item) item.reason = seenReason('viewed', entry.raw);
            var before = results.length;
            addIfEligible(item, cartHandleSet);
            if (results.length > before) viewedAdded++;
            return runLevel2(index + 1, viewedEntries);
          });
        }

        // --- Level 3: Shopify's own recommendations, seeded from the cart ---
        function runLevel3() {
          if (isFull()) return Promise.resolve();
          var seedProductId = getSeedProductId(cart);
          if (!seedProductId) return Promise.resolve(); // empty cart - expected, skip to level 4
          var url =
            '/recommendations/products.json?product_id=' + encodeURIComponent(seedProductId) + '&limit=' + batchLimit;
          return fetchJSON(url).then(function (data) {
            var products = data && Array.isArray(data.products) ? data.products : [];
            products.forEach(function (product) {
              if (isFull()) return;
              addIfEligible(normalizeProduct(product, 'shopify'), cartHandleSet);
            });
          });
        }

        // --- Level 4: fallback collection ---
        function runLevel4() {
          if (isFull()) return Promise.resolve();
          return collectionItems(fallbackCollection, batchLimit).then(function (items) {
            items.forEach(function (item) {
              if (isFull()) return;
              addIfEligible(item, cartHandleSet);
            });
          });
        }

        return runLevel1(0)
          .then(function () {
            if (isFull()) return;
            return runLevel2(0, getFreshViewedByCountThenRecency());
          })
          .then(function () {
            if (isFull()) return;
            return runLevel3();
          })
          .then(function () {
            if (isFull()) return;
            return runLevel4();
          });
      })
      .then(function () {
        return results;
      })
      .catch(function (err) {
        debugLog('getRecommendations failed, returning partial results', err);
        return results;
      });
  }

  // ---------------------------------------------------------------------
  // Memoization
  //
  // Two <cart-recommendations> instances can be live on the same page (the
  // cart-page section + the hidden drawer instance) and each cart mutation can
  // trigger both of them, so the same cascade - up to 8 /products/<handle>.js
  // calls plus /cart.js - would otherwise run twice within milliseconds. The
  // in-flight/resolved promise is cached per limit|fallbackCollection for a
  // short TTL; the second caller joins the first call instead of repeating it.
  //
  // Correctness guard: the cache is dropped as soon as the cart can have
  // changed - on the tracker's mtgreco:cart-changed event and on the UI's
  // explicit invalidateRecommendations() call after its own /cart/add.js - so a
  // stale list (e.g. still recommending a product that was just added) can
  // never be served. The short TTL is only a coalescing window, never the
  // primary freshness mechanism.
  //
  // Note: callers of the same memoized promise share one results array; the
  // renderer only reads it, so no defensive copy is made.
  // ---------------------------------------------------------------------

  var MEMO_TTL_MS = 1500;
  var memoKey = null;
  var memoPromise = null;
  var memoExpiresAt = 0;
  // True while memoPromise is still in flight. The TTL is a coalescing window
  // for repeat callers AFTER a cascade finishes, not a cap on how long a single
  // in-flight cascade may run - so a pending memo is always joined regardless
  // of memoExpiresAt, and the TTL clock only starts once it resolves.
  var memoPending = false;

  function invalidateRecommendations() {
    memoKey = null;
    memoPromise = null;
    memoExpiresAt = 0;
    memoPending = false;
  }

  /**
   * @param {Object} [options]
   * @param {number} [options.limit=3]
   * @param {string} [options.fallbackCollection="commander-staples"]
   * @returns {Promise<Array>} resolves to up to `limit` normalized product objects.
   *   Never rejects - on total failure resolves to whatever was gathered (possibly []).
   */
  function getRecommendations(options) {
    options = options || {};
    var limit =
      typeof options.limit === 'number' && isFinite(options.limit) && options.limit > 0
        ? Math.max(1, Math.floor(options.limit))
        : DEFAULT_LIMIT;
    var fallbackCollection =
      typeof options.fallbackCollection === 'string' && options.fallbackCollection
        ? options.fallbackCollection
        : DEFAULT_COLLECTION;

    var key = limit + '|' + fallbackCollection;
    var nowMs = Date.now();
    // A pending cascade is always joined, however long it has been running;
    // once it has resolved, memoExpiresAt (set below, from completion) gates
    // whether a fresh caller still gets the cached result or starts a new one.
    if (memoPromise && memoKey === key && (memoPending || nowMs < memoExpiresAt)) {
      debugLog('memo hit', key);
      return memoPromise;
    }

    var promise;
    try {
      promise = runCascade(limit, fallbackCollection);
    } catch (err) {
      // A synchronous throw out of runCascade must not break the caller, and
      // must not clobber whatever memo state already existed.
      debugLog('runCascade threw synchronously', err);
      return Promise.resolve([]);
    }

    // Only now, with a real promise in hand, does this become the active memo.
    memoKey = key;
    memoPromise = promise;
    memoPending = true;
    memoExpiresAt = 0;
    promise.then(function () {
      // Guard against a resolution landing after invalidateRecommendations()
      // (or a newer call) already replaced this memo - do not resurrect it.
      if (memoPromise !== promise) return;
      memoPending = false;
      memoExpiresAt = Date.now() + MEMO_TTL_MS;
    });

    return memoPromise;
  }

  try {
    document.addEventListener('mtgreco:cart-changed', invalidateRecommendations);
  } catch (e) {
    debugLog('could not listen for mtgreco:cart-changed', e);
  }

  // ---------------------------------------------------------------------
  // Preporuceno page additions: hydrateHandles, collectionItems
  //
  // collectionItems() is the same fetch+normalize cascade level 4 already
  // used - pulled out so both the cascade and the recommendations page can
  // call it. hydrateHandles() batch-resolves an arbitrary list of handles
  // (the page's scored candidates) into live product cards, concurrency
  // limited and with one 429 retry, in input order, available-only.
  // ---------------------------------------------------------------------

  /**
   * Fetches up to `limit` available, normalized products from a collection
   * (`/collections/<handle>/products.json?limit=`), source 'collection'.
   * Shared by cascade level 4 (fallback recommendations) and by page callers
   * via window.MtgReco.collectionItems.
   */
  function collectionItems(handle, limit) {
    var url = '/collections/' + encodeURIComponent(handle) + '/products.json?limit=' + limit;
    return fetchJSON(url).then(function (data) {
      var products = data && Array.isArray(data.products) ? data.products : [];
      var items = [];
      for (var i = 0; i < products.length && items.length < limit; i++) {
        var item = normalizeProduct(products[i], 'collection');
        if (item) items.push(item);
      }
      return items;
    });
  }

  /**
   * Resolves an arbitrary list of handles (e.g. the recommendations page's
   * scored candidates) into live product cards via /products/<handle>.js,
   * concurrency-limited, with one retry after 1s on HTTP 429. Failures/404s
   * are skipped. Returns only available items, in input order, source
   * 'page'.
   */
  function hydrateHandles(handles, options) {
    var opts = options || {};
    var concurrency = opts.concurrency || 6;
    var results = new Array(handles.length);
    var next = 0;
    function fetchOne(handle, attempt) {
      return fetchJSONWithStatus('/products/' + encodeURIComponent(handle) + '.js').then(function (r) {
        if (r.data === null && attempt === 0 && r.status === 429) {
          return new Promise(function (res) {
            setTimeout(res, 1000);
          }).then(function () {
            return fetchOne(handle, 1);
          });
        }
        var item = normalizeProduct(r.data);
        if (item) item.source = 'page';
        return item;
      });
    }
    function worker() {
      if (next >= handles.length) return Promise.resolve();
      var i = next++;
      return fetchOne(handles[i], 0)
        .then(
          function (item) {
            results[i] = item;
          },
          function () {
            results[i] = null;
          }
        )
        .then(worker);
    }
    var workers = [];
    for (var k = 0; k < Math.min(concurrency, handles.length); k++) workers.push(worker());
    return Promise.all(workers).then(function () {
      return results.filter(function (x) {
        return x && x.available;
      });
    });
  }

  window.MtgReco = window.MtgReco || {};
  window.MtgReco.getRecommendations = getRecommendations;
  window.MtgReco.invalidateRecommendations = invalidateRecommendations;
  window.MtgReco.hydrateHandles = hydrateHandles;
  window.MtgReco.collectionItems = collectionItems;
})();
